import { Node, mergeAttributes } from '@tiptap/core';

import { TiptapNodeType } from '../../tiptapTypes';
import { handleEmptyTitleBackspace, handleTitleEnter } from './taskListTitleKeyboardHandlers';
import { createTitleToTaskInputRule } from './taskListTitleInputRules';

/**
 * The optional title shown as the first line of the single task list editor.
 *
 * Modelled as a dedicated node (rather than a reused paragraph) so the schema itself
 * enforces the constraints we want:
 *   - position: the document content model (`taskListTitle? taskList`) only allows it as
 *     the first child, and at most once.
 *   - no styling: `marks: ''` disallows all inline marks (bold/italic/link/etc.) —
 *     the title is plain text only.
 *   - soft line breaks: `hardBreak` is allowed in the content, so Shift-Enter (bound by
 *     the StarterKit HardBreak extension) inserts a soft break within the single title node.
 *     (Enter/arrow keyboard behaviour is layered on separately.)
 */
export const TaskListTitle = Node.create({
    name: TiptapNodeType.taskListTitle,

    content: '(text | hardBreak)*',

    // Disallow all inline marks so the title can never carry styling.
    marks: '',

    group: 'title',

    // Treat as a defining context so operations like paste replace its content
    // rather than splitting it into a different node type.
    defining: true,

    parseHTML() {
        return [{ tag: 'h2[data-type="task-list-title"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['h2', mergeAttributes(HTMLAttributes, { 'data-type': 'task-list-title' }), 0];
    },

    addKeyboardShortcuts() {
        return {
            /* eslint-disable @typescript-eslint/naming-convention */
            Backspace: () => this.editor.commands.command((props) => handleEmptyTitleBackspace(props, this.name)),
            Enter: () => this.editor.commands.command((props) => handleTitleEnter(props, this.name)),
            /* eslint-enable @typescript-eslint/naming-convention */
        };
    },

    addInputRules() {
        return [createTitleToTaskInputRule(this.name)];
    },
});
